import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MyFormInput,
  MyFormSelect,
  MyFromTextarea,
} from "@/components/zthd/MyFormField";
import { postFetcher, putFetcher } from "@/lib/swr";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { RC_CONCLUSIONS, RcRow } from "@/constants/rc";
import { ArchiveIcon, RocketIcon } from "lucide-react";

// Inline field forms for review-flow (stepper) nodes: each pending node
// surfaces what it needs right on the timeline instead of sending people
// into the edit dialog.

// 待配置节点:操作人贴上 Firebase 链接 = 实验已在控制台开起来了——传了
// confirmTaskId(我是落地任务 assignee)时一步到位:保存链接后自动确认
// 落地任务,状态即进实验中(endDate 只是 due,到期提醒照排)。历史无任务
// 行退化为纯保存。
export const FirebaseLinkInline = ({
  rcId,
  confirmTaskId,
  onSaved,
}: {
  rcId: string;
  /** 落地任务 id(当前用户是 assignee 时传):保存链接后自动确认开实验 */
  confirmTaskId?: string;
  onSaved: () => void;
}) => {
  const { toast } = useToast();
  const [link, setLink] = useState("");
  const { trigger: confirmTask, isMutating: confirming } = useSWRMutation(
    confirmTaskId ? `/rc-tasks/${confirmTaskId}/confirm` : null,
    postFetcher,
    {
      onSuccess: () => {
        toast({ title: "实验已开始", description: "RC 已进入实验中。" });
        onSaved();
      },
      onError: (e: Error) => toastError(toast, "确认配置落地失败", e),
    }
  );
  const { trigger: saveLink, isMutating: saving } = useSWRMutation(
    `/remote-configs/${rcId}`,
    putFetcher,
    {
      onSuccess: () => {
        if (confirmTaskId) confirmTask({ confirmed: true });
        else onSaved();
      },
      onError: (e: Error) => toastError(toast, "保存 Firebase 链接失败", e),
    }
  );
  const isMutating = saving || confirming;
  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      <Input
        value={link}
        onChange={(e) => setLink(e.target.value)}
        placeholder="Firebase 实验链接(实验已开启的凭据)"
        aria-label="Firebase 实验链接"
        className="h-8 w-72 px-2 text-xs"
      />
      <Button
        size="sm"
        disabled={isMutating || !link.trim()}
        onClick={() => saveLink({ rcFirebaseLink: link.trim() })}
      >
        {isMutating
          ? "提交中…"
          : confirmTaskId
          ? "保存并开始实验"
          : "保存"}
      </Button>
    </div>
  );
};

// 数据 report 回填 + 流转(实验中/等待数据回收节点)。按 shadcn Form 规范
// (react-hook-form + zod,复用 MyFormField)。实验结论就是分支开关,按钮
// 跟着结论走,一次点击 = 保存 report + 流转,不设单独的保存:
//   正向 → 提交部署申请;持平 → 部署申请 / 发起回滚;负向 → 发起回滚。
// 非需求方(配在节点上的 BI 只回填不流转)才给纯「保存」。到期后
// (requireLinks)report 文档/ARPU 链接必填——与服务端硬校验同口径。
const reportSchema = (requireLinks: boolean) => {
  const link = requireLinks
    ? z.url("请填写合法链接(到期后必填)")
    : z.url("请填写合法链接").or(z.literal(""));
  return z.object({
    conclusion: z.enum(RC_CONCLUSIONS).or(z.literal("")),
    reportDocLink: link,
    arpuLink: link,
    remarks: z.string(),
  });
};
type ReportValues = z.infer<ReturnType<typeof reportSchema>>;

export const ReportTransitionForm = ({
  rc,
  canTransition,
  requireLinks,
  onDone,
}: {
  rc: RcRow;
  /** 需求方:显示按结论分支的流转按钮;否则只能保存 report */
  canTransition: boolean;
  /** 等待数据回收节点(已到期):链接必填 */
  requireLinks: boolean;
  onDone: () => void;
}) => {
  const { toast } = useToast();
  const form = useForm<ReportValues>({
    resolver: zodResolver(reportSchema(requireLinks)),
    defaultValues: {
      conclusion: (rc.conclusion as ReportValues["conclusion"]) ?? "",
      reportDocLink: rc.reportDocLink ?? "",
      arpuLink: rc.arpuLink ?? "",
      remarks: rc.remarks ?? "",
    },
  });
  const conclusion = form.watch("conclusion");
  const canDeploy = conclusion === "正向" || conclusion === "持平";
  const canClose = conclusion === "持平" || conclusion === "负向";

  const { trigger: saveReport, isMutating: saving } = useSWRMutation(
    `/remote-configs/${rc.id}`,
    putFetcher,
    { onError: (e: Error) => toastError(toast, "保存数据 report 失败", e) }
  );
  const { trigger: deploy, isMutating: deploying } = useSWRMutation(
    `/remote-configs/${rc.id}/deploy`,
    postFetcher,
    {
      onSuccess: () =>
        toast({
          title: "部署申请已提交",
          description: "等待审批通过后由操作人执行部署并确认。",
        }),
      onError: (e: Error) => toastError(toast, "提交部署申请失败", e),
    }
  );
  const { trigger: close, isMutating: closing } = useSWRMutation(
    `/remote-configs/${rc.id}/close`,
    postFetcher,
    {
      onSuccess: () =>
        toast({
          title: "回滚任务已创建",
          description: "等待操作人回收线上配置后确认。",
        }),
      onError: (e: Error) => toastError(toast, "发起回滚失败", e),
    }
  );
  const busy = saving || deploying || closing;

  // 先落 report 再流转;任一步失败已有 toast,后续不再执行
  const run = (action?: "deploy" | "close") =>
    form.handleSubmit(async (v) => {
      try {
        await saveReport({
          conclusion: v.conclusion || null,
          reportDocLink: v.reportDocLink.trim() || null,
          arpuLink: v.arpuLink.trim() || null,
          remarks: v.remarks.trim() || null,
        });
        if (action === "deploy") await deploy({});
        if (action === "close") await close({});
        onDone();
      } catch {
        /* toast 已在各 mutation 的 onError 里给出 */
      }
    })();

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canTransition) run();
        }}
        className="flex w-full max-w-md flex-col gap-2"
      >
        <MyFormSelect
          form={form}
          name="conclusion"
          label="实验结论"
          placeholder="正向 / 负向 / 持平"
          items={RC_CONCLUSIONS.map((c) => ({ code: c, name: c }))}
        />
        <MyFormInput
          form={form}
          name="reportDocLink"
          label={requireLinks ? "report 文档链接(必填)" : "report 文档链接"}
          placeholder="https://…"
        />
        <MyFormInput
          form={form}
          name="arpuLink"
          label={requireLinks ? "ARPU 链接(查数终版,必填)" : "ARPU 链接(查数终版)"}
          placeholder="https://…"
        />
        <MyFromTextarea
          form={form}
          name="remarks"
          label="备注"
          rows={2}
          placeholder="选填"
        />
        <div className="flex items-center gap-1.5">
          {!canTransition ? (
            <Button
              type="submit"
              size="sm"
              disabled={busy || !form.formState.isDirty}
            >
              {saving ? "保存中…" : "保存"}
            </Button>
          ) : !conclusion ? (
            <span className="text-xs text-muted-foreground">
              选择实验结论后出现对应操作:正向/持平 → 部署申请,持平/负向 → 回滚
            </span>
          ) : (
            <>
              {canDeploy && (
                <Button
                  type="button"
                  size="sm"
                  className="gap-1.5"
                  disabled={busy}
                  title="保存 report 并提交部署申请,审批通过后由操作人执行全量部署"
                  onClick={() => run("deploy")}
                >
                  <RocketIcon className="h-3.5 w-3.5" />
                  {deploying ? "提交中…" : "提交部署申请"}
                </Button>
              )}
              {canClose && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={busy}
                  title="保存 report 并创建回滚任务,操作人回收线上配置后确认"
                  onClick={() => run("close")}
                >
                  <ArchiveIcon className="h-3.5 w-3.5" />
                  {closing ? "提交中…" : "发起回滚"}
                </Button>
              )}
            </>
          )}
        </div>
      </form>
    </Form>
  );
};

// 实验结论节点:正向/负向/持平选择即保存
export const ConclusionInline = ({
  rcId,
  onSaved,
}: {
  rcId: string;
  onSaved: () => void;
}) => {
  const { toast } = useToast();
  const { trigger, isMutating } = useSWRMutation(
    `/remote-configs/${rcId}`,
    putFetcher,
    {
      onSuccess: onSaved,
      onError: (e: Error) => toastError(toast, "保存结论失败", e),
    }
  );
  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      <Select
        disabled={isMutating}
        onValueChange={(v) => trigger({ conclusion: v })}
      >
        <SelectTrigger size="sm" className="w-36 text-xs">
          <SelectValue placeholder="补充实验结论" />
        </SelectTrigger>
        <SelectContent>
          {RC_CONCLUSIONS.map((c) => (
            <SelectItem key={c} value={c}>
              {c}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
